import { WritersRoomPhase } from '@/hooks/useWritersRoom';
import { History } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

const PHASE_LABELS: Record<string, string> = {
  snapshot: 'writers_room.phases.snapshot',
  writing: 'writers_room.phases.writing',
  editing: 'writers_room.phases.editing',
  validating: 'writers_room.phases.validating',
  approved: 'writers_room.phases.approved',
};

interface WritersRoomRedoDialogProps {
  redoPhase: WritersRoomPhase | null;
  onClose: () => void;
  onConfirm: () => void;
}

export function WritersRoomRedoDialog({
  redoPhase,
  onClose,
  onConfirm,
}: WritersRoomRedoDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const phaseLabel = redoPhase
    ? t(PHASE_LABELS[redoPhase] ?? 'writers_room.phases.snapshot', redoPhase)
    : '';

  return (
    <Dialog
      open={Boolean(redoPhase)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
          border: 1,
          borderColor: alpha(theme.palette.divider, 0.1),
        },
      }}
    >
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
          }}
        >
          <History sx={{ fontSize: 32, color: 'warning.main' }} />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          {t('editor.redo_phase_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('editor.redo_phase_description', { phase: phaseLabel })}
        </Typography>

        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="warning"
            onClick={onConfirm}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: `0 4px 14px ${alpha(theme.palette.warning.main, 0.4)}`,
            }}
          >
            {t('common.confirm_redo')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
